/**
 * The prestige shop as DATA (v0.4 RFC §5) — permanent account-level SPECIAL
 * UPGRADES bought with Reputation Capital. Same data-driven discipline as
 * data/letters: entries are inert data; the read-sites (run-init for head-start,
 * the two expansion sites for faster-climb) interpret them. Its only RUNTIME
 * import is tunables (the AccountState import is type-only, erased at compile —
 * no runtime cycle with GameState). Numbers are placeholders (v0.8 owns balance).
 *
 * Efficiency (income / relapse / offline perks) is a documented THIRD category
 * in the RFC; its read-sites arrive with v0.8 balance, so v0.4 ships only the
 * two categories whose sites the RFC §9 verification names — no dead perks.
 */

import { CARE_TIERS, PRESTIGE, upgradeCost, type CareTierId } from "@/data/tunables";
import type { AccountState } from "@/state/GameState";

export type UpgradeCategory = "headStart" | "fasterClimb" | "criminal";
export type UpgradeCurrency = "reputationCapital" | "streetKnowledge";

/** Typed perk effects — read at the site named in each variant's comment. */
export type UpgradeEffect =
  | { kind: "seedFunding"; perLevel: number } // run-init: starting funding
  | { kind: "veteranRecruiter" } // run-init: start the Recruiter at owned level
  | { kind: "prelevelAutomator"; tier: CareTierId } // run-init: that tier's automator
  | { kind: "expansionPriceDiscount"; perLevel: number } // makePurchasable: −frac/level
  | { kind: "requirementDiscount"; perLevel: number } // completionsAtFrontier: −frac/level
  | { kind: "startCriminalUnlocked" }; // run-init: begin with the criminal track open (v0.6)

export interface UpgradeDef {
  id: string;
  title: string;
  /** Draft satire copy (v0.9 owns the final pass). */
  blurb: string;
  category: UpgradeCategory;
  /** Which prestige currency prices it (default Reputation Capital; v0.6 adds SK). */
  currency?: UpgradeCurrency;
  /** Currency for the level 0→1 buy; grows by costGrowth per owned level. */
  baseCost: number;
  costGrowth: number;
  maxLevel: number;
  effect: UpgradeEffect;
}

export const UPGRADES = [
  {
    id: "seedFunding",
    title: "Founder's Grant",
    blurb: "Open each new facility with seed capital from a foundation named after you.",
    category: "headStart",
    baseCost: 3,
    costGrowth: 1.8,
    maxLevel: 5,
    effect: { kind: "seedFunding", perLevel: 250 },
  },
  {
    id: "veteranRecruiter",
    title: "Rolodex",
    blurb: "Your Recruiter follows you between facilities, already dialing.",
    category: "headStart",
    baseCost: 5,
    costGrowth: 2.2,
    maxLevel: 3,
    effect: { kind: "veteranRecruiter" },
  },
  {
    id: "standingDetoxCrew",
    title: "Standing Detox Crew",
    blurb: "A Technician is on the payroll before the first bed is even installed.",
    category: "headStart",
    baseCost: 6,
    costGrowth: 2.4,
    maxLevel: 3,
    effect: { kind: "prelevelAutomator", tier: "detox" },
  },
  {
    id: "cheaperExpansion",
    title: "Regional Development Officer",
    blurb: "Your pre-approved growth partner waives a slice of every expansion fee.",
    category: "fasterClimb",
    baseCost: 4,
    costGrowth: 2.0,
    maxLevel: 5,
    effect: { kind: "expansionPriceDiscount", perLevel: 0.08 },
  },
  {
    id: "easierRequirements",
    title: "Expedited Accreditation",
    blurb: "The board needs fewer completions to bless your next expansion.",
    category: "fasterClimb",
    baseCost: 4,
    costGrowth: 2.0,
    maxLevel: 4,
    effect: { kind: "requirementDiscount", perLevel: 0.1 },
  },
  // ── Street-Knowledge-priced criminal perks (v0.6 §11.3 = option a) ──
  {
    id: "dirtyMoney",
    title: "Dirty Money",
    blurb: "Every new facility opens with a little laundered seed capital. Don't ask.",
    category: "criminal",
    currency: "streetKnowledge",
    baseCost: 3,
    costGrowth: 1.9,
    maxLevel: 5,
    effect: { kind: "seedFunding", perLevel: 400 },
  },
  {
    id: "madeMan",
    title: "Made Man",
    blurb: "You don't wait for the proposition anymore — the back-office ledger opens on day one.",
    category: "criminal",
    currency: "streetKnowledge",
    baseCost: 6,
    costGrowth: 1,
    maxLevel: 1,
    effect: { kind: "startCriminalUnlocked" },
  },
] as const satisfies readonly UpgradeDef[];

export type UpgradeId = (typeof UPGRADES)[number]["id"];

const BY_ID = new Map<string, UpgradeDef>(UPGRADES.map((u) => [u.id, u]));

/** Fail-loud lookup — an unknown id is a programming error, not a fallback. */
export function upgradeDef(id: string): UpgradeDef {
  const def = BY_ID.get(id);
  if (!def) {
    throw new Error(`upgradeDef: unknown upgrade id "${id}"`);
  }
  return def;
}

/** Owned level (absent = 0). */
export function upgradeLevel(account: AccountState, id: UpgradeId): number {
  return account.upgrades[id] ?? 0;
}

/** Reputation-Capital cost to buy the NEXT level (priced at the current level). */
export function upgradeCostAt(account: AccountState, id: UpgradeId): number {
  const def = upgradeDef(id);
  return upgradeCost(def.baseCost, def.costGrowth, upgradeLevel(account, id));
}

// ── aggregate reads (interpreted at the RFC-named sites) ────────────────────

/** Sum a numeric per-level effect across owned upgrades of a matching kind. */
function sumEffect(account: AccountState, match: (e: UpgradeEffect) => number): number {
  let total = 0;
  for (const def of UPGRADES) {
    const level = upgradeLevel(account, def.id);
    if (level > 0) total += match(def.effect) * level;
  }
  return total;
}

/** Head-start: extra starting funding for a fresh run. */
export function seedFundingBonus(account: AccountState): number {
  return sumEffect(account, (e) => (e.kind === "seedFunding" ? e.perLevel : 0));
}

/** Head-start: the Recruiter level a fresh run begins at (0 = none). */
export function veteranRecruiterLevel(account: AccountState): number {
  return sumEffect(account, (e) => (e.kind === "veteranRecruiter" ? 1 : 0));
}

/** Head-start: the automator level `tier` begins a fresh run at (0 = none). */
export function prelevelAutomator(account: AccountState, tier: CareTierId): number {
  return sumEffect(account, (e) => (e.kind === "prelevelAutomator" && e.tier === tier ? 1 : 0));
}

/** Faster-climb: multiplier on expansion PRICE (floored so it never zeroes). */
export function expansionPriceMultiplier(account: AccountState): number {
  const off = sumEffect(account, (e) => (e.kind === "expansionPriceDiscount" ? e.perLevel : 0));
  return Math.max(PRESTIGE.priceDiscountFloor, 1 - off);
}

/** Faster-climb: multiplier on REQUIRED completions (floored so it never zeroes). */
export function requirementMultiplier(account: AccountState): number {
  const off = sumEffect(account, (e) => (e.kind === "requirementDiscount" ? e.perLevel : 0));
  return Math.max(PRESTIGE.requirementDiscountFloor, 1 - off);
}

/** Every CARE_TIERS automator a head-start touches (for run-init iteration). */
export function headStartAutomatorTiers(): readonly CareTierId[] {
  return CARE_TIERS;
}

/** Head-start (v0.6): begin a run with the criminal track already unlocked. */
export function startsCriminalUnlocked(account: AccountState): boolean {
  return sumEffect(account, (e) => (e.kind === "startCriminalUnlocked" ? 1 : 0)) > 0;
}

/** Which prestige currency an upgrade is priced in (default Reputation Capital). */
export function upgradeCurrency(def: UpgradeDef): UpgradeCurrency {
  return def.currency ?? "reputationCapital";
}
